import React, { useState } from 'react';
import { z } from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ShieldCheck } from 'lucide-react';
import { trackSellerRequestSubmitted } from '@/utils/contactTracking';

const schema = z.object({
  buyer_name: z.string().trim().min(2, 'Min. 2 caractere').max(120),
  buyer_phone: z.string().trim().min(6, 'Telefon prea scurt').max(40),
  buyer_email: z.string().trim().email('Email invalid').max(255),
  message: z.string().trim().max(1000).optional(),
});

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  listingId: string;
  listingTitle: string;
  sellerId?: string | null;
  sellerType?: string | null;
}

export const ReservationModal: React.FC<Props> = ({ open, onOpenChange, listingId, listingTitle, sellerId, sellerType }) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ buyer_name: '', buyer_phone: '', buyer_email: '', message: '' });
  // One key per filled-in request: a retry after a network error reuses it, so the
  // server saves one row and the success is counted once. New key after reset.
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [keyContent, setKeyContent] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast({
        title: 'Verificați datele',
        description: Object.values(parsed.error.flatten().fieldErrors).flat().join(' · '),
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    // The key belongs to one exact content. Edited form after a failed try = new request, new key.
    const fingerprint = JSON.stringify([listingId, parsed.data]);
    let key = requestKey;
    if (keyContent && keyContent !== fingerprint) {
      key = crypto.randomUUID();
      setRequestKey(key);
    }
    setKeyContent(fingerprint);
    try {
      const { data, error } = await supabase.functions.invoke('send-listing-reservation', {
        body: { listing_id: listingId, ...parsed.data, client_request_id: key },
      });
      if (error) {
        const status = (error as any)?.context?.status;
        if (status === 409) {
          // Same key, different content: never reuse the old success silently.
          setRequestKey(crypto.randomUUID());
          setKeyContent(null);
          toast({
            title: 'Cererea anterioară are alte date',
            description: 'Nu am retrimis-o. Apasă din nou „Trimite” pentru a trimite o cerere nouă cu datele actuale.',
            variant: 'destructive',
          });
          return;
        }
        throw error;
      }
      if (!data?.ok || !data?.id) throw new Error('Cererea nu a fost confirmată');
      // Numărăm cererea doar după confirmarea serverului.
      trackSellerRequestSubmitted({ id: listingId, sellerId, sellerType }, String(data.id));
      setDone(true);
      toast({ title: 'Cerere înregistrată', description: 'Cererea ta a fost salvată și transmisă vânzătorului.' });
    } catch (err: any) {
      console.error(err);
      toast({ title: 'Nu am putut trimite', description: err?.message || 'Reîncearcă', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setForm({ buyer_name: '', buyer_phone: '', buyer_email: '', message: '' }); setDone(false); setRequestKey(crypto.randomUUID()); setKeyContent(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Trimite cerere către dealer</DialogTitle>
          <DialogDescription>
            {listingTitle}. Cererea ta este transmisă vânzătorului, care te poate contacta la datele introduse.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-foreground">Cerere înregistrată</h3>
            <p className="text-sm text-muted-foreground">Am salvat cererea și o transmitem vânzătorului. El te va contacta la datele introduse.</p>
            <Button onClick={() => onOpenChange(false)} className="w-full">Închide</Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="r_name">Nume complet</Label>
              <Input id="r_name" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="r_phone">Telefon</Label>
                <Input id="r_phone" type="tel" value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="r_email">Email</Label>
                <Input id="r_email" type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} required />
              </div>
            </div>
            <div>
              <Label htmlFor="r_msg">Mesaj opțional</Label>
              <Textarea id="r_msg" rows={3} placeholder="Ex: aș dori să vin sâmbătă să o văd" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>

            <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
              Serviciu gratuit. Dealerul primește datele tale și te contactează direct pentru a stabili vizionarea sau condițiile rezervării.
            </div>

            <Button type="submit" disabled={submitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90" size="lg">
              {submitting ? 'Se trimite...' : 'Trimite cererea de rezervare'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
